"use client";

import ProgressTrackerChip from "@/app/components/cards/ProgressTrackerChip";
import {
  getUserUnlockResourceAccessDescriptor,
  getUserUnlockResourceCTAHref,
} from "@/app/components/cards/ResourceNotAvailablePlaceholder";
import { UserAccess } from "@/lib/nextAuth/userPermissions";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { getLoginUrl } from "@/lib/utils/routing";
import { userHasAdminRights } from "@/lib/utils/pageChecks";
import { QuestionTestStateSchema } from "@/types/redux-schema";
import { Box, Button, Card, Collapse, Group, Loader } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import LockRounded from "@mui/icons-material/LockRounded";
import { Question } from "@tsTypes/question-schema";
import { TestStates } from "@tsTypes/test-schema";
import { checkIfMultipleChoiceQuestion, roundTo2DP } from "@utils/helpers";
import clsx from "clsx";
import { getUserRole, getUserVerificationStatus } from "lib/utils/accessChecks";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from "react";
import {
  selectTestQuestionState,
  updateTestQuestionState,
} from "../../../../../../../../features/activeTestSessions/activeTestSessionsSlice";
import {
  questionAttempt,
  selectQuestionById,
} from "../../../../../../../../features/progress/subTopicUserReduxMetadataSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../../../lib/redux/hooks";
import QuestionHeader from "./Header/QuestionHeader";
import ResourceEditInStrapiButton from "./Header/ResourceEditInStrapiButton";
import QuestionBody from "./Question/QuestionBody";
import classes from "./QuestionModule.module.css";
import ReportBtn from "./ReportBtn";
const AnswerBody = dynamic(() => import("./Answer/AnswerBody"));

type QuestionProps = {
  question: Question;
  indx: number | string;
  subjectNameLower: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  subTopicDocumentId: string;
  hiddenByFilters: boolean;
  userAccess: UserAccess;
  currentSubTheme?: string;
  testStatus?: TestStates;
  isFlagged?: boolean;
  testId?: string;
  updateTestQuestionState?: (questionId: string, questionState: any) => void;
  isPreviewPortal?: true;
  adminHeaderExtra?: ReactNode;
};

export const useQuestionStyles = ({
  userHasAdminEditAccess,
  shouldHideQuestion,
  topicDocumentId,
  testId,
  isPreviewPortal,
}: {
  userHasAdminEditAccess: boolean;
  shouldHideQuestion: boolean;
  topicDocumentId: string | undefined;
  testId: string | undefined;
  isPreviewPortal?: true;
}) => {
  const headerBoxClassName = clsx(
    classes.headerBox,
    userHasAdminEditAccess && classes.headerBoxAdmin,
    (shouldHideQuestion || isPreviewPortal) && classes.headerBoxHidden
  );
  const cardClassName = clsx(
    classes.card,
    shouldHideQuestion && classes.cardHidden,
    topicDocumentId &&
      (testId ? classes.cardWithSmallBottomMargin : classes.cardWithBottomMargin)
  );
  const computedClasses: Record<string, string> = {
    ...(classes as unknown as Record<string, string>),
    headerBox: headerBoxClassName,
    card: cardClassName,
  };
  return { classes: computedClasses };
};

export default function QuestionModule({
  question,
  indx,
  subjectNameLower,
  topicDocumentId,
  subjectDocumentId,
  subTopicDocumentId,
  hiddenByFilters,
  currentSubTheme,
  testStatus,
  userAccess,
  isFlagged,
  testId,
  isPreviewPortal,
  adminHeaderExtra,
}: QuestionProps) {
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const { data: session } = useSession();
  const userRole = getUserRole(session);
  const userHasAdminEditAccess =
    userHasAdminRights(userRole) || userRole === "Content Creator";
  const userUnlockResourceAccessDescriptor =
    getUserUnlockResourceAccessDescriptor(
      session,
      getUserVerificationStatus(session)
    );
  const isLoginCTA = userUnlockResourceAccessDescriptor === "login to unlock";

  const dispatch = useCustomDispatch();
  const isMultipleChoiceQuestion = checkIfMultipleChoiceQuestion(question);
  const questionReduxMetadata = useCustomSelector((state) =>
    selectQuestionById(
      state,
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      question.documentId
    )
  );
  // Get question state from Redux if in test mode
  const questionTestState = useCustomSelector((state) =>
    testId
      ? selectTestQuestionState(state, testId, question.documentId)
      : undefined
  );

  // Setup local state - use test state if available
  const [selectedAnswer, setSelectedAnswer] = useState<string>(
    questionTestState?.selectedOption !== undefined
      ? questionTestState.selectedOption
      : ""
  );
  const [marked, setMarked] = useState<number | undefined>(
    questionTestState?.marks !== undefined ? questionTestState.marks : undefined
  );
  const [opened, { toggle: toggleMarkscheme }] = useDisclosure(
    (testStatus === "marking" && !isMultipleChoiceQuestion) ||
      testStatus === "reviewing"
  );

  const shouldHideQuestion = checkIfShouldHideQuestion(
    testStatus,
    hiddenByFilters,
    currentSubTheme,
    question.RelatedSubThemes
  );

  const { classes: questionClasses } = useQuestionStyles({
    userHasAdminEditAccess,
    shouldHideQuestion,
    topicDocumentId,
    testId,
    isPreviewPortal,
  });

  // Sync local state with Redux state changes
  useEffect(() => {
    if (!questionTestState) {
      // Redux state was cleared, reset local state
      setSelectedAnswer("");
      setMarked(undefined);
      if (opened) {
        toggleMarkscheme();
      }
    } else {
      // Sync with Redux state
      setMarked(questionTestState.marks);
      testStatus === "marking" &&
        !isMultipleChoiceQuestion &&
        !opened &&
        toggleMarkscheme();
    }
  }, [questionTestState]);

  function handleAnswerInteraction(answer: string, partId?: string) {
    // update local state for question itself
    setSelectedAnswer(answer);
    // handle test state updates
    if (testId) {
      let updates: Partial<QuestionTestStateSchema> = {};
      if (isMultipleChoiceQuestion) {
        updates = {
          selectedOption: answer,
        };
      } else if (partId) {
        updates = {
          textAnswers: {
            [partId]: answer,
          },
        };
      }
      dispatch(
        updateTestQuestionState({
          testId,
          questionId: question.documentId,
          questionState: updates,
        })
      );
    }
  }

  return (
    <>
      <Box className={questionClasses.headerBox}>
        {userHasAdminEditAccess && !isPreviewPortal && (
          <Group gap="xs">
            <ResourceEditInStrapiButton
              resource={"questions"}
              resourceDocumentId={question.documentId}
            />
            {adminHeaderExtra}
          </Group>
        )}
        {((topicDocumentId && !testStatus) || testStatus === "reviewing") && (
          <ProgressTrackerChip
            resource="questions"
            resourceReduxMeta={questionReduxMetadata}
            subjectName={subjectNameLower}
            subjectDocumentId={subjectDocumentId}
            noResource={!question}
            disableActions={userAccess !== "Full Access"}
          />
        )}
      </Box>
      <Card id={question.documentId.toString()} className={questionClasses.card}>
        <QuestionHeader
          subjectColorIndex={subjectColorIndex}
          questionDocumentId={question.documentId}
          questionDifficulty={question.Difficulty}
          indx={indx}
          requiresCalc={getCorrectedQuestionCalcRequirement(
            subjectNameLower,
            question
          )}
          testId={testId}
          isFlagged={isFlagged}
          testStatus={testStatus}
        />
        <QuestionBody
          questionDocumentId={question.documentId}
          question={question.Question}
          questionMeta={question.QuestionStyle[0]}
          handleAnswerInteraction={handleAnswerInteraction}
          selectedAnswer={selectedAnswer}
          opened={opened}
          marked={marked}
          testStatus={testStatus}
          questionTestState={questionTestState}
        />
        {!testStatus && (
          <Button
            component={userAccess !== "Full Access" ? Link : undefined}
            href={
              userAccess !== "Full Access"
                ? getUserUnlockResourceCTAHref(
                    userUnlockResourceAccessDescriptor
                  )
                : "#"
            }
            aria-label={
              userAccess !== "Full Access"
                ? "Check access"
                : opened
                ? "Reset"
                : "Submit"
            }
            className={classes.button}
            color="darkblue"
            disabled={
              ["LOADING", "No Access"].includes(userAccess)
                ? false
                : isMultipleChoiceQuestion && !selectedAnswer
            }
            onClick={() => {
              if (isLoginCTA && typeof window !== "undefined") {
                window.location.href = getLoginUrl();
                return;
              }
              if (userAccess !== "Full Access") {
                return;
              } else {
                opened
                  ? resetQuestion(
                      question.documentId,
                      isMultipleChoiceQuestion,
                      setSelectedAnswer,
                      setMarked,
                      opened,
                      toggleMarkscheme
                    )
                  : markQuestionAnswer(
                      question.documentId,
                      subjectDocumentId,
                      topicDocumentId,
                      subTopicDocumentId,
                      userAccess !== "Full Access",
                      isMultipleChoiceQuestion,
                      selectedAnswer,
                      setMarked,
                      toggleMarkscheme,
                      dispatch,
                      question.QuestionStyle[0]?.CorrectOption?.CorrectOption
                    );
              }
            }}
            leftSection={
              userAccess === "LOADING" ? (
                <Loader size="sm" type="dots" color="white" />
              ) : userAccess === "No Access" ? (
                <LockRounded style={{ width: 20, height: 20 }} />
              ) : undefined
            }
            classNames={{ section: classes.buttonLeftIcon }}
          >
            {userAccess === "LOADING"
              ? "Checking access..."
              : userAccess === "Full Access"
              ? opened
                ? "Reset"
                : "Submit"
              : userUnlockResourceAccessDescriptor.replace("unlock", "mark")}
          </Button>
        )}
        {userAccess === "Full Access" && (
          <Collapse in={opened}>
            <AnswerBody
              questionMeta={question.QuestionStyle[0]}
              questionMarks={question.TotalMarks}
              questionTip={question.UsefulTip}
              selectedAnswer={selectedAnswer}
              submitLongAnswerMark={submitLongAnswerMark}
              marked={marked}
              setMarked={setMarked}
              questionDocumentId={question.documentId}
              subjectDocumentId={subjectDocumentId}
              topicDocumentId={topicDocumentId}
              subTopicDocumentId={subTopicDocumentId}
              disableActions={userAccess !== "Full Access"}
              dispatch={dispatch}
              testId={testId}
            />
            <ReportBtn questionDocumentId={question.documentId} />
          </Collapse>
        )}
      </Card>
    </>
  );
}

function getCorrectedQuestionCalcRequirement(
  subjectNameLower: string,
  question: Question
) {
 // if its a maths AI question then we use the field in strapi
 if (subjectNameLower.includes("maths aa")) {
  return question.RequiresCalc
 }
 return true;
}

function markQuestionAnswer(
  questionDocumentId: string,
  subjectDocumentId: string | undefined,
  topicDocumentId: string | undefined,
  subTopicDocumentId: string | undefined,
  disableActions: boolean,
  isMultipleChoiceQuestion: boolean,
  selectedAnswer: string,
  setMarked: Dispatch<SetStateAction<number | undefined>>,
  toggleMarkscheme: () => void,
  dispatch: any,
  CorrectOption?: string
) {
  if (isMultipleChoiceQuestion) {
    setMarked(selectedAnswer === CorrectOption ? 1 : 0);
    submitMcAnswerMark(
      disableActions,
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      questionDocumentId,
      selectedAnswer,
      CorrectOption,
      dispatch
    );
    toggleMarkscheme();
  } else {
    toggleMarkscheme();
  }
}

export function submitMcAnswerMark(
  disableActions: boolean,
  subjectDocumentId: string | undefined,
  topicDocumentId: string | undefined,
  subTopicDocumentId: string | undefined,
  questionDocumentId: string,
  selectedAnswer: string | undefined,
  CorrectOption: string | undefined,
  dispatch: any
) {
  if (
    !disableActions &&
    subjectDocumentId &&
    topicDocumentId &&
    subTopicDocumentId
  ) {
    submitQuestionPerformance(
      selectedAnswer === CorrectOption ? 100 : 0,
      true,
      questionDocumentId,
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      dispatch
    );
  }
}

export function submitLongAnswerMark(
  userSelectedMark: number,
  questionTotalMarks: number,
  questionDocumentId: string,
  subjectDocumentId: string | undefined,
  topicDocumentId: string | undefined,
  subTopicDocumentId: string | undefined,
  disableActions: boolean,
  dispatch: any
) {
  if (
    disableActions ||
    !subjectDocumentId ||
    !topicDocumentId ||
    !subTopicDocumentId
  ) {
    return;
  }
  const percentagePerformance = roundTo2DP(
    (userSelectedMark / questionTotalMarks) * 100
  );
  submitQuestionPerformance(
    percentagePerformance,
    false,
    questionDocumentId,
    subjectDocumentId,
    topicDocumentId,
    subTopicDocumentId,
    dispatch
  );
}

function submitQuestionPerformance(
  percentagePerformance: number,
  isMultipleChoiceQuestion: boolean,
  questionDocumentId: string,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  dispatch: any
) {
  dispatch(
    questionAttempt({
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      questionDocumentId,
      isMultipleChoice: isMultipleChoiceQuestion,
      percentagePerformance,
      date: Date.now(),
    })
  );
}

function resetQuestion(
  questionDocumentId: string,
  isMultipleChoiceQuestion: boolean,
  setSelectedAnswer: Dispatch<SetStateAction<string>>,
  setMarked: Dispatch<SetStateAction<number | undefined>>,
  opened: boolean,
  toggleMarkscheme: () => void
) {
  if (isMultipleChoiceQuestion) {
    setSelectedAnswer("");
    setMarked(undefined);
  } else {
    document
      .getElementById(questionDocumentId)
      ?.querySelectorAll(".questionTextInputArea .mantine-Textarea-input")
      .forEach((input) => ((input as HTMLTextAreaElement).value = ""));
    setMarked(undefined);
  }
  opened && toggleMarkscheme();
}

function checkIfShouldHideQuestion(
  testStatus: TestStates | undefined,
  hiddenByFilters: boolean,
  currentSubTheme: string | undefined,
  questionRelatedSubThemes: Question["RelatedSubThemes"]
) {
  if (hiddenByFilters) return true;
  let shouldHideQuestion = false;
  let questionSubThemes = questionRelatedSubThemes?.map((st) => st.documentId);
  if (!testStatus) {
    if (
      questionSubThemes &&
      currentSubTheme &&
      currentSubTheme !== "all" &&
      !questionSubThemes.includes(currentSubTheme)
    ) {
      shouldHideQuestion = true;
    }
  }
  return shouldHideQuestion;
}
